import { z, ZodObject, ZodTypeAny, ZodRawShape, ZodOptional, ZodReadonly, ZodNullable } from "zod";

/**
 * Recursively extracts readonly fields from a schema.
 */
function extractReadonlyFields<T extends ZodObject<any>>(schema: T, path = ""): Set<string> {
  const shape = schema._def.shape();
  let readonlyFields = new Set<string>();

  for (const key in shape) {
    const field = shape[key];

    const fullPath = path ? `${path}.${key}` : key;

    if ("readonly" in field._def) {
      readonlyFields.add(fullPath); 
    }

    if (field instanceof z.ZodObject) {
      const nestedReadonlyFields = extractReadonlyFields(field, fullPath);
      nestedReadonlyFields.forEach((f) => readonlyFields.add(f));
    }
  }

  return readonlyFields;
}

/**
 * Compares two objects and returns the differences, skipping readonly fields.
 */
 function getDifferences<T extends Record<string, any>>(
  obj1: T,
  obj2: T,
  schema: ZodObject<ZodRawShape>
): Partial<T> {
  const readonlyKeys = extractReadonlyFields(schema);
  const differences: Partial<T> = {};

  function checkDifferences(o1: any, o2: any, currentPath = ""): any {
    if (readonlyKeys.has(currentPath)) return; // Skip readonly fields

    if (Array.isArray(o1) && Array.isArray(o2)) {
      if (JSON.stringify(o1) !== JSON.stringify(o2)) {
        return o2;
      }
    } else if (typeof o1 === "object" && typeof o2 === "object" && o1 !== null && o2 !== null) {
      let nestedDiffs: any = {};

      const allKeys = {...o2, ...o1}       

      for (const key in allKeys) {
        const nextPath = currentPath ? `${currentPath}.${key}` : key;
        const diff = checkDifferences(o1[key], o2[key], nextPath);

        if(diff?.isDiff){
          nestedDiffs[key] = diff?.newValue
        }else if (diff !== undefined){
          nestedDiffs[key] = diff
        }
      }

      return Object.keys(nestedDiffs).length > 0 ? nestedDiffs : undefined;
    } else if (o1 !== o2) {
      return {isDiff: true, oldValue: o1, newValue: o2}
    }
  }

  const allKeys = {...obj2, ...obj1} 
  
  for (const key in allKeys) {
    const diff = checkDifferences(obj1[key], obj2[key], key);

    if(diff?.isDiff){
      differences[key] = diff?.newValue
    }else if (diff !== undefined){
      differences[key] = diff
    }
  }

  return differences;
}

export function removeReadonlys<T extends object>(obj: T, schema: ZodObject<ZodRawShape>): Partial<T> {
  const result = {} as Partial<T>;

  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

    const schemaDef = schema.shape[key] as ZodTypeAny | undefined;
    if (!schemaDef) continue; // Ignore fields not in schema

    // Unwrap optional/nullable to check for readonly
    let unwrappedSchema = schemaDef;
    while (unwrappedSchema instanceof ZodOptional || unwrappedSchema instanceof ZodNullable) {
      unwrappedSchema = unwrappedSchema._def.innerType;
    }

    // Skip readonly fields
    if (unwrappedSchema instanceof ZodReadonly) {
      continue;
    }

    // If it's a nested object, recursively remove readonly fields
    if (unwrappedSchema instanceof ZodObject && typeof obj[key] === "object" && obj[key] !== null) {
      result[key as keyof T] = removeReadonlys(obj[key] as any, unwrappedSchema as ZodObject<ZodRawShape>) as any;
    } else {
      result[key as keyof T] = obj[key];
    }
  }

  return result;
}


export const schemaHandler = { getDifferences, removeReadonlys }